import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Clock, Loader2, AlertTriangle, X,
  CheckCircle, FileText, Music, Film, ImageIcon,
  ShieldCheck, BadgeCheck, Zap, Heart, ChevronLeft, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useSystemTheme() {
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = (dark) => document.documentElement.classList.toggle('dark', dark);
    apply(mq.matches);
    const handler = (e) => apply(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
}

function monthsOnPlatform(createdAt) {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  const now = new Date();
  const months = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
  if (months < 1) return 'New creator';
  if (months === 1) return '1 month on platform';
  if (months < 12) return `${months} months on platform`;
  const yrs = Math.floor(months / 12);
  return `${yrs} year${yrs > 1 ? 's' : ''} on platform`;
}

// ── Fullscreen Viewer ─────────────────────────────────────────────────────────

const FullscreenViewer = ({ content, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const file = content[idx];

  const prev = () => setIdx(i => (i > 0 ? i - 1 : content.length - 1));
  const next = () => setIdx(i => (i < content.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="fullscreen-viewer">
      {/* Close */}
      <button onClick={onClose} className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors" data-testid="fullscreen-close-btn">
        <X className="h-5 w-5 text-white" />
      </button>

      {/* Counter */}
      {content.length > 1 && (
        <div className="absolute top-4 left-4 z-20 px-3 py-1.5 rounded-full text-xs text-white/60 bg-white/10">
          {idx + 1} / {content.length}
        </div>
      )}

      {/* Prev */}
      {content.length > 1 && (
        <button onClick={prev} className="absolute left-3 z-20 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors" data-testid="fullscreen-prev-btn">
          <ChevronLeft className="h-5 w-5 text-white" />
        </button>
      )}

      {/* Content — centered */}
      <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-12" onClick={onClose}>
        <div onClick={e => e.stopPropagation()} className="flex items-center justify-center">
          {file.type === 'image' && (
            <img
              src={`${API_URL}${file.url}`}
              alt={file.name}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg block"
              data-testid="fullscreen-image"
            />
          )}
          {file.type === 'video' && (
            <video
              src={`${API_URL}${file.url}`}
              controls
              autoPlay
              className="max-w-[90vw] max-h-[85vh] rounded-lg block"
              data-testid="fullscreen-video"
            />
          )}
          {file.type === 'audio' && (
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-white/[0.06] border border-white/10">
                <Music className="h-10 w-10 text-white/50" />
              </div>
              <p className="text-white/50 text-sm mb-4">{file.name}</p>
              <audio src={`${API_URL}${file.url}`} controls autoPlay className="w-80" />
            </div>
          )}
          {(!['image', 'video', 'audio'].includes(file.type)) && (
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-white/[0.06] border border-white/10">
                <FileText className="h-10 w-10 text-white/50" />
              </div>
              <p className="text-white/50 text-sm">{file.name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Next */}
      {content.length > 1 && (
        <button onClick={next} className="absolute right-3 z-20 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors" data-testid="fullscreen-next-btn">
          <ChevronRight className="h-5 w-5 text-white" />
        </button>
      )}
    </div>
  );
};

// ── Abuse Report Modal ────────────────────────────────────────────────────────

const AbuseReportModal = ({ onClose, sessionId }) => {
  const [form, setForm] = useState({ reporter_name: '', reporter_email: '', description: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.reporter_name.trim() || !form.reporter_email.trim() || !form.description.trim()) {
      setError('Please fill in all fields.'); return;
    }
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API_URL}/api/reports/abuse`, { ...form, session_id: sessionId, page_url: window.location.href });
      setSubmitted(true);
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/85 backdrop-blur-sm">
      <div className="relative w-full max-w-sm rounded-2xl p-6 bg-white dark:bg-[#111] border border-neutral-200 dark:border-white/[0.08]" data-testid="abuse-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 dark:text-white/30 hover:text-neutral-600 dark:hover:text-white/60 transition-colors" data-testid="close-modal-btn">
          <X className="h-4 w-4" />
        </button>
        {submitted ? (
          <div className="text-center py-4" data-testid="report-success">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
              <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
            </div>
            <h3 className="text-neutral-900 dark:text-white font-semibold mb-2">Report Received</h3>
            <p className="text-neutral-500 dark:text-white/40 text-sm leading-relaxed">Your issue will be resolved within <span className="text-neutral-700 dark:text-white/60">24 hours</span>.</p>
            <button onClick={onClose} className="mt-5 px-6 py-2 rounded-full text-sm text-neutral-500 dark:text-white/50 border border-neutral-200 dark:border-white/10 hover:border-neutral-400 dark:hover:border-white/20 transition-all" data-testid="close-success-btn">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400" />
              <h3 className="text-neutral-900 dark:text-white font-semibold text-sm">Report Abuse</h3>
            </div>
            <p className="text-neutral-500 dark:text-white/35 text-xs mb-4 leading-relaxed">Encountered something inappropriate? Let us know and we'll investigate within 24 hours.</p>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="abuse-form">
              {[['reporter_name', 'text', 'Your Name', 'John Doe'], ['reporter_email', 'email', 'Your Email', 'you@example.com']].map(([key, type, label, ph]) => (
                <div key={key}>
                  <label className="block text-neutral-500 dark:text-white/35 text-xs mb-1">{label}</label>
                  <input type={type} placeholder={ph} value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full rounded-xl px-3.5 py-2.5 text-sm text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-white/20 bg-neutral-50 dark:bg-white/[0.05] border border-neutral-200 dark:border-white/10 focus:border-neutral-400 dark:focus:border-white/25 focus:outline-none transition-colors"
                    data-testid={`${key}-input`} />
                </div>
              ))}
              <div>
                <label className="block text-neutral-500 dark:text-white/35 text-xs mb-1">What happened?</label>
                <textarea placeholder="Describe the issue..." value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} className="w-full rounded-xl px-3.5 py-2.5 text-sm text-neutral-900 dark:text-white placeholder-neutral-300 dark:placeholder-white/20 bg-neutral-50 dark:bg-white/[0.05] border border-neutral-200 dark:border-white/10 focus:border-neutral-400 dark:focus:border-white/25 focus:outline-none transition-colors resize-none"
                  data-testid="reporter-description-input" />
              </div>
              {error && <p className="text-red-500 dark:text-red-400/70 text-xs" data-testid="report-error">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-2.5 rounded-xl text-sm font-medium transition-all border bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-300/80 disabled:opacity-50"
                data-testid="submit-report-btn">
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useSystemTheme();
  const [content, setContent] = useState([]);
  const [linkInfo, setLinkInfo] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [purchaseInfo, setPurchaseInfo] = useState(null);
  const [expiresIn, setExpiresIn] = useState(24);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [tipping, setTipping] = useState(false);
  const [tipError, setTipError] = useState('');
  const [tipSuccess, setTipSuccess] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState('');
  const carouselRef = useRef(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (searchParams.get('tip') === 'success') setTipSuccess(true);
  }, [searchParams]);

  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) favicon.href = '/favicon.svg';
    const verify = async () => {
      if (!sessionId) { setError('No session ID found'); setLoading(false); return; }
      try {
        const res = await axios.get(`${API_URL}/api/purchases/verify/${sessionId}`);
        setContent(res.data.content || []);
        setLinkInfo(res.data.link || null);
        setCreatorInfo(res.data.creator_info || null);
        setPurchaseInfo(res.data.purchase || null);
        setExpiresIn(res.data.expires_in_hours || 24);
        const storedEmail = localStorage.getItem('buyer_email');
        if (storedEmail) setBuyerEmail(storedEmail);
        document.title = `${res.data.link?.title || 'Content Unlocked'} | Unlocky`;
      } catch { setError('Could not verify your purchase. Please contact support.'); }
      finally { setLoading(false); }
    };
    verify();
    return () => { document.title = 'VFans Media'; if (favicon) favicon.href = '/vfans-logo.svg'; };
  }, [sessionId]);

  const handleCarouselScroll = (e) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.offsetWidth);
    if (idx !== currentIndex) setCurrentIndex(idx);
  };

  const scrollTo = (i) => {
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: i * carouselRef.current.offsetWidth, behavior: 'smooth' });
    }
  };

  const handleTip = async () => {
    const amt = parseFloat(tipAmount);
    if (!amt || amt < 5) { setTipError('Minimum tip is $5.00'); return; }
    setTipping(true); setTipError('');

    // Try one-click tip if we have buyer email
    if (buyerEmail) {
      try {
        await axios.post(`${API_URL}/api/tips/quick`, {
          email: buyerEmail, amount: amt, link_id: linkInfo?.id,
        });
        setTipSuccess(true);
        setTipAmount('');
        setTipping(false);
        return;
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (!detail.includes('No saved card')) {
          setTipError(detail || 'Failed to process tip. Please try again.');
          setTipping(false);
          return;
        }
        // No saved card — fall through to Stripe redirect
      }
    }

    // Fallback: Stripe checkout redirect
    try {
      const { data } = await axios.post(`${API_URL}/api/tips/checkout`, {
        amount: amt, link_id: linkInfo?.id, creator_id: linkInfo?.creator_id,
        user_id: linkInfo?.user_id, organization_id: linkInfo?.organization_id, session_id: sessionId,
      });
      window.location.href = data.url;
    } catch (err) {
      setTipError(err.response?.data?.detail || 'Failed to process tip. Please try again.');
      setTipping(false);
    }
  };

  const imgCount = content.filter(f => f.type === 'image').length;
  const vidCount = content.filter(f => f.type === 'video').length;
  const audioCount = content.filter(f => f.type === 'audio').length;
  const otherCount = content.length - imgCount - vidCount - audioCount;
  const creatorInitial = creatorInfo?.name?.charAt(0)?.toUpperCase() || 'C';
  const timeOnPlatform = monthsOnPlatform(creatorInfo?.created_at);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-black" data-testid="payment-success-loading">
      <Loader2 className="h-7 w-7 animate-spin text-neutral-300 dark:text-white/30" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-5 bg-neutral-50 dark:bg-black" data-testid="payment-success-error">
      <div className="text-center max-w-xs p-8 rounded-3xl bg-white dark:bg-white/[0.04] border border-neutral-200 dark:border-white/[0.07]">
        <h1 className="text-neutral-900 dark:text-white font-semibold mb-2">Something went wrong</h1>
        <p className="text-neutral-500 dark:text-white/35 text-sm mb-6">{error}</p>
        <button onClick={() => navigate('/')} className="px-5 py-2 rounded-full text-neutral-600 dark:text-white/70 text-sm border border-neutral-200 dark:border-white/10 hover:border-neutral-400 dark:hover:border-white/20 transition-all" data-testid="go-home-btn">Go Home</button>
        <p className="mt-5">
          <button onClick={() => setShowReportModal(true)} className="text-[10px] text-neutral-400 dark:text-white/25 hover:text-neutral-600 dark:hover:text-white/50 transition-colors underline" data-testid="report-abuse-btn-error">Need help? Report abuse</button>
        </p>
      </div>
      {showReportModal && <AbuseReportModal onClose={() => setShowReportModal(false)} sessionId={sessionId} />}
    </div>
  );

  return (
    <>
      <style>{`
        .carousel-scroll::-webkit-scrollbar { display: none; }
        .thumb-active-s { border: 2px solid #10b981 !important; }
        .thumb-item-s { border: 2px solid transparent; transition: border-color 0.2s; cursor: pointer; }
        .thumb-item-s:hover { border-color: rgba(16,185,129,0.4); }
        .tip-btn {
          background: linear-gradient(135deg, #f472b6 0%, #ec4899 100%);
          box-shadow: 0 4px 24px rgba(236,72,153,0.35), inset 0 1px 0 rgba(255,255,255,0.2);
          transition: all 0.25s;
        }
        .tip-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 32px rgba(236,72,153,0.45); }
        .tip-btn:active:not(:disabled) { transform: translateY(0); }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn { 0% { transform:scale(0.5); opacity:0; } 70% { transform:scale(1.1); } 100% { transform:scale(1); opacity:1; } }
        .fade-up { animation: fadeUp 0.4s ease both; }
        .fade-up-1 { animation-delay: 0.05s; }
        .fade-up-2 { animation-delay: 0.1s; }
        .fade-up-3 { animation-delay: 0.15s; }
        .pop-in { animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both; }
      `}</style>

      <div className="min-h-screen bg-neutral-50 dark:bg-black flex flex-col items-center pb-10" data-testid="payment-success-page">
        <div className="w-full max-w-sm px-4 pt-6">

          {/* Content carousel */}
          {content.length > 0 ? (
            <div
              ref={carouselRef}
              className="carousel-scroll relative rounded-3xl mb-3 fade-up cursor-pointer bg-neutral-200 dark:bg-[#111] overflow-hidden"
              style={{ height: '65vw', maxHeight: '300px', overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
              onScroll={handleCarouselScroll}
              data-testid="content-viewer"
            >
              <div style={{ display: 'flex', height: '100%', width: `${content.length * 100}%` }}>
                {content.map((file, i) => (
                  <div key={i} onClick={() => setFullscreenIndex(i)} className="bg-neutral-100 dark:bg-[#0a0a0a]" style={{ width: `${100 / content.length}%`, height: '100%', position: 'relative', flexShrink: 0, scrollSnapAlign: 'start', cursor: 'pointer' }}>
                    {file.type === 'image' && (
                      <img src={`${API_URL}${file.url}`} alt={file.name} className="w-full h-full object-contain block" data-testid={`content-image-${i}`} />
                    )}
                    {file.type === 'video' && (
                      <video src={`${API_URL}${file.url}`} controls onClick={e => e.stopPropagation()} className="w-full h-full object-contain block" data-testid={`content-video-${i}`} />
                    )}
                    {file.type === 'audio' && (
                      <div className="w-full h-full flex flex-col items-center justify-center p-6">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-neutral-200 dark:bg-white/[0.06] border border-neutral-300 dark:border-white/10">
                          <Music className="w-7 h-7 text-neutral-400 dark:text-white/50" />
                        </div>
                        <p className="text-neutral-500 dark:text-white/40 text-xs mb-4 text-center">{file.name}</p>
                        <audio src={`${API_URL}${file.url}`} controls onClick={e => e.stopPropagation()} className="w-full" data-testid={`content-audio-${i}`} />
                      </div>
                    )}
                    {(!['image', 'video', 'audio'].includes(file.type)) && (
                      <div className="w-full h-full flex flex-col items-center justify-center p-6">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-neutral-200 dark:bg-white/[0.06] border border-neutral-300 dark:border-white/10">
                          <FileText className="w-7 h-7 text-neutral-400 dark:text-white/50" />
                        </div>
                        <p className="text-neutral-500 dark:text-white/40 text-xs text-center">{file.name}</p>
                      </div>
                    )}

                    {i === 0 && (
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium pop-in bg-emerald-500/15 border border-emerald-500/30 text-emerald-500 dark:text-emerald-300 backdrop-blur-sm">
                          <CheckCircle className="h-3 w-3" /> Unlocked
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="relative rounded-3xl overflow-hidden mb-3 fade-up flex items-center justify-center bg-gradient-to-b from-neutral-200 to-neutral-100 dark:from-[#1a1a2e] dark:to-[#0f0f1a]"
              style={{ height: '65vw', maxHeight: '300px' }} data-testid="content-viewer">
              <div className="text-center">
                <CheckCircle className="h-8 w-8 text-emerald-500 dark:text-emerald-400 mx-auto mb-3 pop-in" />
                <p className="text-neutral-500 dark:text-white/50 text-sm">Content unlocked</p>
              </div>
            </div>
          )}

          {/* Thumbnail strip */}
          {content.length > 1 && (
            <div className="flex gap-2 mb-5 overflow-x-auto pb-1 fade-up fade-up-1" style={{ scrollbarWidth: 'none' }} data-testid="thumbnail-strip">
              {content.map((file, i) => (
                <button key={i} onClick={() => scrollTo(i)}
                  className={`flex-shrink-0 rounded-xl overflow-hidden thumb-item-s relative bg-neutral-200 dark:bg-[#111] ${i === currentIndex ? 'thumb-active-s' : ''}`}
                  style={{ width: 72, height: 72 }}
                  data-testid={`thumb-${i}`}>
                  {(file.type === 'image' || file.type === 'video') ? (
                    <img src={`${API_URL}${file.thumb_url || file.url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-neutral-100 dark:bg-white/[0.04]">
                      {file.type === 'audio' ? <Music className="h-5 w-5 text-neutral-400 dark:text-white/30" /> : <FileText className="h-5 w-5 text-neutral-400 dark:text-white/30" />}
                    </div>
                  )}
                  <div className="absolute bottom-1 right-1 bg-black/55 backdrop-blur rounded-md px-1.5 py-0.5 flex items-center justify-center">
                    {file.type === 'video' ? <Film className="w-3 h-3 text-white" /> : file.type === 'audio' ? <Music className="w-3 h-3 text-white" /> : <ImageIcon className="w-3 h-3 text-white" />}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Info card */}
          <div className="rounded-3xl overflow-hidden fade-up fade-up-2 bg-white dark:bg-white/[0.04] border border-neutral-200 dark:border-white/[0.07]">

            {/* Creator row */}
            {creatorInfo && (
              <>
                <a href={`/c/${creatorInfo.username || creatorInfo.id}`} className="flex items-center gap-3 px-5 py-4 hover:bg-neutral-50 dark:hover:bg-white/[0.02] transition-colors" data-testid="creator-profile-link">
                  <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center bg-neutral-100 dark:bg-white/[0.08] border border-neutral-200 dark:border-white/10">
                    {creatorInfo.profile_picture
                      ? <img src={creatorInfo.profile_picture.startsWith('/api') ? `${API_URL}${creatorInfo.profile_picture}` : creatorInfo.profile_picture} alt="" className="w-full h-full object-cover" />
                      : <span className="text-sm font-semibold text-neutral-400 dark:text-white/50">{creatorInitial}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-neutral-900 dark:text-white text-sm leading-none" data-testid="creator-name">{creatorInfo.name || 'Creator'}</span>
                      <BadgeCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-neutral-500 dark:text-white/35 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Verified</span>
                      {timeOnPlatform && (
                        <>
                          <span className="text-neutral-300 dark:text-white/20 text-[10px]">·</span>
                          <span className="text-[11px] text-neutral-500 dark:text-white/35 flex items-center gap-1"><Clock className="w-3 h-3" />{timeOnPlatform}</span>
                        </>
                      )}
                    </div>
                  </div>
                </a>
                <div className="h-px bg-neutral-100 dark:bg-white/[0.05]" />
              </>
            )}

            {/* Title & meta */}
            <div className="px-5 py-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center pop-in bg-emerald-50 dark:bg-emerald-500/12 border border-emerald-200 dark:border-emerald-500/25">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400" />
                </div>
                <span className="text-emerald-600 dark:text-emerald-400 text-xs font-semibold uppercase tracking-wider">Purchase Confirmed</span>
              </div>

              <h1 className="text-base font-bold text-neutral-900 dark:text-white mb-3 leading-snug" data-testid="link-title">{linkInfo?.title || 'Your Content'}</h1>

              <div className="flex items-center gap-2 flex-wrap mb-1">
                {imgCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <ImageIcon className="w-3 h-3" />{imgCount} image{imgCount !== 1 ? 's' : ''}
                  </span>
                )}
                {vidCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <Film className="w-3 h-3" />{vidCount} video{vidCount !== 1 ? 's' : ''}
                  </span>
                )}
                {audioCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <Music className="w-3 h-3" />{audioCount} audio
                  </span>
                )}
                {otherCount > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-neutral-500 dark:text-white/45 bg-neutral-100 dark:bg-white/[0.06] border border-neutral-200 dark:border-white/[0.08]">
                    <FileText className="w-3 h-3" />{otherCount} file{otherCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {linkInfo?.description && <p className="text-xs text-neutral-500 dark:text-white/35 mt-2 leading-relaxed" data-testid="link-description">{linkInfo.description}</p>}
            </div>

            <div className="h-px bg-neutral-100 dark:bg-white/[0.05]" />

            {/* Tip section */}
            <div className="px-5 py-5 fade-up fade-up-3">

              {purchaseInfo?.amount && (
                <p className="text-3xl font-bold text-neutral-900 dark:text-white mb-1 leading-none" data-testid="amount-paid">
                  ${purchaseInfo.amount.toFixed(2)} <span className="text-sm font-normal text-neutral-400 dark:text-white/30 ml-0.5">USD</span>
                </p>
              )}

              <div className="mb-5 mt-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] bg-amber-50 dark:bg-amber-500/[0.06] border border-amber-200 dark:border-amber-500/12 text-amber-600 dark:text-amber-300/55">
                  <Clock className="h-3 w-3" /> Available for {expiresIn}h
                </span>
              </div>

              {tipSuccess && (
                <div className="mb-4 px-4 py-3 rounded-2xl flex items-center gap-3 bg-pink-50 dark:bg-pink-500/[0.08] border border-pink-200 dark:border-pink-500/15" data-testid="tip-success-msg">
                  <Heart className="h-4 w-4 text-pink-500 dark:text-pink-400 flex-shrink-0" />
                  <p className="text-pink-700 dark:text-pink-300/80 text-xs">Thank you for your tip! The creator appreciates your support.</p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs font-medium text-neutral-500 dark:text-white/35 mb-1.5 uppercase tracking-wider">Tip the creator</label>
                <div className="flex gap-2 mb-3">
                  {[5, 10, 20, 50].map(amt => (
                    <button key={amt} onClick={() => { setTipAmount(String(amt)); setTipError(''); }}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all border ${
                        tipAmount === String(amt)
                          ? 'bg-pink-50 dark:bg-pink-500/15 border-pink-200 dark:border-pink-500/30 text-pink-600 dark:text-pink-400'
                          : 'bg-neutral-50 dark:bg-white/[0.06] border-neutral-200 dark:border-white/[0.08] text-neutral-500 dark:text-white/50'
                      }`}
                      data-testid={`tip-preset-${amt}`}>
                      ${amt}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-white/30 text-sm font-medium">$</span>
                  <input
                    type="number" min="5" step="1" placeholder="Custom amount (min $5)" value={tipAmount}
                    onChange={e => { setTipAmount(e.target.value); setTipError(''); }}
                    className="w-full pl-8 pr-4 py-3 rounded-xl text-sm bg-neutral-50 dark:bg-white/[0.06] text-neutral-900 dark:text-white border border-neutral-200 dark:border-white/10 placeholder:text-neutral-300 dark:placeholder:text-white/25 focus:border-pink-400 dark:focus:border-pink-500/50 focus:outline-none focus:ring-2 focus:ring-pink-500/10 transition-all"
                    data-testid="tip-amount-input"
                  />
                </div>
              </div>

              {tipError && <p className="text-red-500 dark:text-red-400/80 text-xs mb-3" data-testid="tip-error">{tipError}</p>}

              <button onClick={handleTip} disabled={tipping || !tipAmount}
                className="tip-btn w-full py-4 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="tip-btn">
                {tipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
                {tipping ? 'Processing...' : tipAmount && parseFloat(tipAmount) >= 5
                  ? (buyerEmail ? `Send $${parseFloat(tipAmount).toFixed(2)} Tip ⚡` : `Send $${parseFloat(tipAmount).toFixed(2)} Tip`)
                  : 'Send Tip'}
              </button>

              <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-center gap-5 text-[11px] text-neutral-400 dark:text-white/25">
                  <span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" />Secure payment</span>
                  <span className="flex items-center gap-1.5"><Zap className="w-3 h-3" />Instant access</span>
                </div>
              </div>

              <p className="text-center text-[10px] text-neutral-400 dark:text-white/20 mt-4">
                By tipping, you agree to our{' '}
                <a href="/terms-of-service" className="underline text-neutral-500 dark:text-white/35 hover:text-neutral-700 dark:hover:text-white/50 transition-colors">Terms of Service</a>
              </p>

              <div className="mt-3 text-center">
                <button onClick={() => setShowReportModal(true)} className="text-[10px] text-neutral-400 dark:text-white/30 hover:text-neutral-600 dark:hover:text-white/55 transition-colors underline underline-offset-2 decoration-neutral-300 dark:decoration-white/15" data-testid="report-abuse-btn">
                  Need help? Report abuse
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {fullscreenIndex !== null && (
        <FullscreenViewer content={content} startIndex={fullscreenIndex} onClose={() => setFullscreenIndex(null)} />
      )}

      {showReportModal && <AbuseReportModal onClose={() => setShowReportModal(false)} sessionId={sessionId} />}
    </>
  );
};

export default PaymentSuccess;
